import { Css } from "@homebound/beam";
import { useState } from "react";
import { renderAccordion } from "src/routes/libraries/versioning/VersionHistoryUtils";
import { count } from "src/utils";
import {
  ProductOfferingTlivChangeSummaryFragment,
  useProductOfferingVersionTlivDetailsQuery,
} from "../ProductOfferingVersionHistory.generated";

type ProductTlivChangesProps = {
  versionId: string;
  productTlivChangeSummary: ProductOfferingTlivChangeSummaryFragment[];
};

// Product-material takeoff line item changes. The count comes from the cheap pre-aggregated
// `productTlivChangeSummary`; the per-row detail (location / item / product) is only fetched on
// drill-in (when the accordion is expanded) so large offerings don't load every `tliVersions`
// row up front. See ON-276.
export function ProductTlivChanges(props: ProductTlivChangesProps) {
  const { versionId, productTlivChangeSummary } = props;
  const [expanded, setExpanded] = useState(false);

  const total = productTlivChangeSummary.sum((s) => s.total);
  if (total === 0) return null;

  return renderAccordion(
    count(total, "Product update"),
    <ProductTlivDetails versionId={versionId} expanded={expanded} />,
    () => setExpanded(true),
  );
}

type ProductTlivDetailsProps = { versionId: string; expanded: boolean };

// Fires the drill-in query once the accordion has been expanded (skipped until then), then renders
// the product-material rows as "location: item: product".
function ProductTlivDetails(props: ProductTlivDetailsProps) {
  const { versionId, expanded } = props;
  const { data, loading } = useProductOfferingVersionTlivDetailsQuery({
    variables: { id: versionId },
    skip: !expanded,
  });

  if (loading || !data) return <div css={Css.md.$}>Loading…</div>;

  const productRows = data.readyPlanAggregateVersion.tliVersions.filter((tliv) => tliv.identity.isProductMaterial);

  return (
    <ul css={Css.m0.$}>
      {productRows.map((tliv) => (
        <li key={tliv.id} css={Css.md.$}>
          {`${tliv.location.effectiveLocation.name}: ${tliv.identity.item.name}: ${tliv.materialVariant?.displayName ?? ""}`}
        </li>
      ))}
    </ul>
  );
}
